import React, { useState } from 'react'
import { backendService, UserType } from '../../services/backendService'
import { logInfo, logError } from '../../utils/errorLogger'
import { APP_VERSION } from '../../version'
import './LoginPage.css'
import logoShort from '../../images/Logo Short.jpg'

interface LoginPageProps {
  onLoginSuccess: (userId: string, userName: string, authToken: string, userType: UserType, isAdmin?: boolean) => void
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password')
      logError('Login validation failed: Missing credentials', 'Login')
      return
    }

    setLoading(true)
    setError(null)

    try {
      logInfo('Attempting smart login', 'Login')

      const loginData = await backendService.smartLogin(username.trim(), password.trim())

      if (loginData.success) {
        const isAdmin = loginData.isAdmin || false
        logInfo(`Login successful (userType: ${loginData.userType}, admin: ${isAdmin})`, 'Login')
        onLoginSuccess(loginData.userId, loginData.userName, loginData.authToken, loginData.userType, isAdmin)
      } else {
        throw new Error('Login failed')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      logError(`Smart login failed: ${errorMessage}`, 'Login')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="logo">
            <img src={logoShort} alt="Girasoles" aria-hidden="true" style={{ width: 64, height: 64, borderRadius: '50%' }} />
            <p>Professional Access</p>
          </div>
        </div>

        <form className="login-form" onSubmit={handleLogin}>
          <h2>Sign In</h2>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="username">Username / Email</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username or email"
              disabled={loading}
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-with-icon">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                disabled={loading}
                autoComplete="current-password"
                aria-describedby="password-visibility-toggle"
              />
              <button
                id="password-visibility-toggle"
                type="button"
                className="toggle-password"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                onClick={() => setShowPassword(v => !v)}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="login-btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <div className="login-footer">
          <p>Girasoles CAD Workbench v{APP_VERSION}</p>
        </div>
      </div>
    </div>
  )
}
